import { useState } from 'react';
import { post, put } from '../api';
import { ContentSectionEditor } from './ContentSectionEditor';

function Input({ label, value, onChange, placeholder, type = 'text', rows }) {
  const s = { width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>}
      {rows ? <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={{ ...s, resize: 'vertical' }} /> : <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={s} />}
    </div>
  );
}

function Btn({ onClick, children, variant = 'primary', small, disabled, style = {} }) {
  const styles = {
    primary: { background: '#c9a96e', color: '#1a1208', border: 'none' },
    secondary: { background: 'transparent', color: '#8a7055', border: '1px solid #3a2a12' },
    danger: { background: '#e63c3c22', color: '#e63c3c', border: '1px solid #e63c3c44' },
    success: { background: '#10b98122', color: '#10b981', border: '1px solid #10b98144' },
    ghost: { background: '#c9a96e18', color: '#c9a96e', border: '1px solid #c9a96e44' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...styles[variant], borderRadius: 8, padding: small ? '6px 14px' : '10px 22px', fontWeight: 700, cursor: disabled ? 'default' : 'pointer', fontSize: small ? 12 : 14, opacity: disabled ? 0.5 : 1, fontFamily: 'inherit', transition: 'all 0.15s', ...style }}>
      {children}
    </button>
  );
}

function Modal({ children, onClose, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 14, padding: 32, maxWidth: wide ? 860 : 660, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.7)' }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ── Add/Edit Module ───────────────────────────────────────────────────────────
export function ModuleEditor({ module, onClose, onSave, userRole }) {
  const isNew = !module;
  const [form, setForm] = useState({
    title: module?.title || '',
    category: module?.category || 'Operations',
    description: module?.description || '',
    pass_mark: module?.pass_mark || 80,
    hands_on_required: module?.hands_on_required ?? 1,
    status: module?.status || 'draft',
    min_role: module?.min_role || 'staff',
  });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const categories = ['Foundation', 'Operations', 'Activity', 'Safety', 'Systems', 'Bar', 'Kitchen', 'Events', 'Management'];

  async function save() {
    if (!form.title) return setMsg('Title is required');
    setLoading(true);
    try {
      if (isNew) {
        await post('/modules', form);
        setMsg(userRole === 'owner' ? '✓ Module created as Draft!' : '✓ Request submitted for owner approval!');
      } else {
        await put(`/modules/${module.id}`, form);
        setMsg(userRole === 'owner' ? '✓ Module updated!' : '✓ Change request submitted for owner approval!');
      }
      setTimeout(() => { onSave(); onClose(); }, 1200);
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setLoading(false); }
  }

  return (
    <Modal onClose={onClose} wide>
      <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>
        {isNew ? 'Add New Training Module' : `Edit: ${module.title}`}
      </h3>
      {userRole !== 'owner' && <p style={{ color: '#f59e0b', fontSize: 12, marginBottom: 20 }}>⚠ As GM, your changes will be submitted for Kevin's approval before going live.</p>}

      <Input label="Module Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Bartender Certification" />
      <Input label="Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief overview of what this module covers" rows={2} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div>
          <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Category</label>
          <input list="category-suggestions" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="Type a category or pick one" style={{ width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }} />
          <datalist id="category-suggestions">
            {categories.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div>
          <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pass Mark %</label>
          <input type="number" value={form.pass_mark} onChange={e => setForm(p => ({ ...p, pass_mark: Number(e.target.value) }))} min={50} max={100} style={{ width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</label>
          <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} style={{ width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit' }}>
            <option value="draft">Draft (hidden from staff)</option>
            <option value="published">Published (visible to staff)</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Hands-On Sign-Off Required?</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[{ v: 1, label: 'Yes — manager must sign off' }, { v: 0, label: 'No — quiz only' }].map(o => (
            <div key={o.v} onClick={() => setForm(p => ({ ...p, hands_on_required: o.v }))} style={{ padding: 12, borderRadius: 8, cursor: 'pointer', border: `1px solid ${form.hands_on_required === o.v ? '#c9a96e' : '#3a2a12'}`, background: form.hands_on_required === o.v ? '#c9a96e18' : '#2a1f0f', color: form.hands_on_required === o.v ? '#f5e6c8' : '#8a7055', textAlign: 'center', fontSize: 13 }}>{o.label}</div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Who Can See This Module?</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {[{ v: 'staff', label: 'All Staff' }, { v: 'lead', label: 'Leads & Above' }, { v: 'admin', label: 'Managers Only' }].map(o => (
            <div key={o.v} onClick={() => setForm(p => ({ ...p, min_role: o.v }))} style={{ padding: 12, borderRadius: 8, cursor: 'pointer', border: `1px solid ${form.min_role === o.v ? '#c9a96e' : '#3a2a12'}`, background: form.min_role === o.v ? '#c9a96e18' : '#2a1f0f', color: form.min_role === o.v ? '#f5e6c8' : '#8a7055', textAlign: 'center', fontSize: 13 }}>{o.label}</div>
          ))}
        </div>
        <p style={{ color: '#5a4030', fontSize: 11, marginTop: 6 }}>Managers Only = hidden from regular staff and leads; appears once someone is promoted to GM/Manager. Owners and managers always see every module in the editor.</p>
      </div>

      {msg && <p style={{ color: msg.startsWith('✓') ? '#10b981' : '#e63c3c', fontSize: 13, marginBottom: 12 }}>{msg}</p>}
      <div style={{ display: 'flex', gap: 10 }}>
        <Btn onClick={save} disabled={loading || !form.title}>{loading ? 'Saving...' : isNew ? 'Create Module' : 'Save Changes'}</Btn>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ── Add/Edit Quiz Question ────────────────────────────────────────────────────
export function QuestionEditor({ moduleId, question, onClose, onSave, userRole }) {
  const isNew = !question;
  const [form, setForm] = useState({
    question: question?.question || '',
    options: question?.options || ['', '', '', ''],
    correct_index: question?.correct_index ?? 0,
  });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  function setOption(i, val) {
    const opts = [...form.options];
    opts[i] = val;
    setForm(p => ({ ...p, options: opts }));
  }

  function addOption() {
    if (form.options.length >= 6) return;
    setForm(p => ({ ...p, options: [...p.options, ''] }));
  }

  function removeOption(i) {
    if (form.options.length <= 2) return;
    const opts = form.options.filter((_, idx) => idx !== i);
    setForm(p => ({ ...p, options: opts, correct_index: Math.min(p.correct_index, opts.length - 1) }));
  }

  async function save() {
    if (!form.question) return setMsg('Question text is required');
    if (form.options.some(o => !o.trim())) return setMsg('All answer options must be filled in');
    setLoading(true);
    try {
      if (isNew) {
        await post(`/modules/${moduleId}/questions`, form);
      } else {
        await put(`/modules/${moduleId}/questions/${question.id}`, form);
      }
      setMsg(userRole === 'owner' ? '✓ Saved!' : '✓ Submitted for approval!');
      setTimeout(() => { onSave(); onClose(); }, 1000);
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setLoading(false); }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>
        {isNew ? 'Add Quiz Question' : 'Edit Question'}
      </h3>
      {userRole !== 'owner' && <p style={{ color: '#f59e0b', fontSize: 12, marginBottom: 16 }}>⚠ Will be submitted for Kevin's approval.</p>}

      <Input label="Question" value={form.question} onChange={e => setForm(p => ({ ...p, question: e.target.value }))} placeholder="e.g. What is the minimum age for axe throwing?" rows={2} />

      <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Answer Options (click the correct answer)</label>
      {form.options.map((opt, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <div onClick={() => setForm(p => ({ ...p, correct_index: i }))} style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${form.correct_index === i ? '#10b981' : '#3a2a12'}`, background: form.correct_index === i ? '#10b981' : 'transparent', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a1208', fontSize: 14, fontWeight: 700 }}>
            {form.correct_index === i ? '✓' : ''}
          </div>
          <input value={opt} onChange={e => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`} style={{ flex: 1, background: form.correct_index === i ? '#10b98118' : '#2a1f0f', border: `1px solid ${form.correct_index === i ? '#10b98144' : '#3a2a12'}`, borderRadius: 8, padding: '8px 12px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit' }} />
          {form.options.length > 2 && <button onClick={() => removeOption(i)} style={{ background: 'none', border: 'none', color: '#e63c3c', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}>×</button>}
        </div>
      ))}
      {form.options.length < 6 && <button onClick={addOption} style={{ background: 'none', border: '1px dashed #3a2a12', borderRadius: 8, color: '#8a7055', padding: '8px 16px', cursor: 'pointer', fontSize: 13, marginBottom: 16, fontFamily: 'inherit' }}>+ Add Option</button>}

      {msg && <p style={{ color: msg.startsWith('✓') ? '#10b981' : '#e63c3c', fontSize: 13, marginBottom: 12 }}>{msg}</p>}
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <Btn onClick={save} disabled={loading}>Save Question</Btn>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ── Add Video ─────────────────────────────────────────────────────────────────
export function VideoEditor({ moduleId, onClose, onSave, userRole }) {
  const [form, setForm] = useState({ title: '', url: '', duration: '' });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  async function save() {
    if (!form.title || !form.url) return setMsg('Title and URL are required');
    setLoading(true);
    try {
      await post(`/modules/${moduleId}/videos`, form);
      setMsg(userRole === 'owner' ? '✓ Video added!' : '✓ Submitted for approval!');
      setTimeout(() => { onSave(); onClose(); }, 1000);
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setLoading(false); }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>Add Training Video</h3>
      {userRole !== 'owner' && <p style={{ color: '#f59e0b', fontSize: 12, marginBottom: 16 }}>⚠ Will be submitted for Kevin's approval.</p>}
      <Input label="Video Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Axe Safety Demonstration" />
      <Input label="YouTube / Vimeo Embed URL" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://www.youtube.com/embed/..." />
      <p style={{ color: '#5a4030', fontSize: 11, marginTop: -10, marginBottom: 14 }}>In YouTube: Share → Embed → copy the src URL only</p>
      <Input label="Duration (optional)" value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))} placeholder="e.g. 8 min" />
      {msg && <p style={{ color: msg.startsWith('✓') ? '#10b981' : '#e63c3c', fontSize: 13, marginBottom: 12 }}>{msg}</p>}
      <div style={{ display: 'flex', gap: 10 }}>
        <Btn onClick={save} disabled={loading}>Add Video</Btn>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ── Add Document ──────────────────────────────────────────────────────────────
export function DocumentEditor({ moduleId, onClose, onSave, userRole }) {
  const [form, setForm] = useState({ title: '', url: '', file_type: 'PDF', category: 'General' });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  async function save() {
    if (!form.title || !form.url) return setMsg('Title and URL are required');
    setLoading(true);
    try {
      await post(`/modules/${moduleId}/documents`, form);
      setMsg(userRole === 'owner' ? '✓ Document added!' : '✓ Submitted for approval!');
      setTimeout(() => { onSave(); onClose(); }, 1000);
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setLoading(false); }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>Add Document / SOP</h3>
      {userRole !== 'owner' && <p style={{ color: '#f59e0b', fontSize: 12, marginBottom: 16 }}>⚠ Will be submitted for Kevin's approval.</p>}
      <Input label="Document Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Allergen Policy 2024" />
      <Input label="Document URL (OneDrive, Google Drive, etc.)" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://..." />
      <p style={{ color: '#5a4030', fontSize: 11, marginTop: -10, marginBottom: 14 }}>Paste a shareable link from OneDrive or Google Drive</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
        {[{ label: 'File Type', key: 'file_type', options: ['PDF', 'DOC', 'XLS', 'Video', 'Link'] }, { label: 'Category', key: 'category', options: ['General', 'Safety', 'HR', 'Operations', 'Bar', 'Kitchen', 'Activity'] }].map(f => (
          <div key={f.key}>
            <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{f.label}</label>
            <select value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={{ width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit' }}>
              {f.options.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        ))}
      </div>
      {msg && <p style={{ color: msg.startsWith('✓') ? '#10b981' : '#e63c3c', fontSize: 13, marginBottom: 12 }}>{msg}</p>}
      <div style={{ display: 'flex', gap: 10 }}>
        <Btn onClick={save} disabled={loading}>Add Document</Btn>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ── Full Admin Content Panel ──────────────────────────────────────────────────
export function ContentEditor({ modules, onRefresh, userRole }) {
  const [selectedModule, setSelectedModule] = useState(null);
  const [showModuleEditor, setShowModuleEditor] = useState(false);
  const [showQuestionEditor, setShowQuestionEditor] = useState(false);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [showDocEditor, setShowDocEditor] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [contentModule, setContentModule] = useState(null);

  const catColor = { Foundation: '#c9a96e', Operations: '#f59e0b', Activity: '#e63c3c', Safety: '#10b981', Systems: '#6366f1', Bar: '#a855f7', Kitchen: '#f97316', Events: '#06b6d4' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 4 }}>Content Editor</h2>
          <p style={{ color: '#8a7055', fontSize: 13 }}>
            {userRole === 'owner' ? 'As Owner, your changes go live immediately.' : 'As GM, your changes are submitted to Kevin for approval.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {userRole === 'owner' && <a href={`/api/export/all-content?token=${localStorage.getItem('tnt_token')}`} target="_blank" rel="noreferrer" style={{ background: '#6366f122', color: '#6366f1', border: '1px solid #6366f144', borderRadius: 8, padding: '10px 18px', textDecoration: 'none', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>⬇ Export All Content</a>}
          <Btn onClick={() => { setEditingModule(null); setShowModuleEditor(true); }}>+ New Module</Btn>
        </div>
      </div>

      {showModuleEditor && <ModuleEditor module={editingModule} onClose={() => { setShowModuleEditor(false); setEditingModule(null); }} onSave={onRefresh} userRole={userRole} />}
      {contentModule && <ContentSectionEditor module={contentModule} onClose={() => setContentModule(null)} onSave={onRefresh} userRole={userRole} />}
      {showQuestionEditor && selectedModule && <QuestionEditor moduleId={selectedModule.id} question={editingQuestion} onClose={() => { setShowQuestionEditor(false); setEditingQuestion(null); }} onSave={onRefresh} userRole={userRole} />}
      {showVideoEditor && selectedModule && <VideoEditor moduleId={selectedModule.id} onClose={() => setShowVideoEditor(false)} onSave={onRefresh} userRole={userRole} />}
      {showDocEditor && selectedModule && <DocumentEditor moduleId={selectedModule.id} onClose={() => setShowDocEditor(false)} onSave={onRefresh} userRole={userRole} />}

      <div style={{ display: 'grid', gap: 16 }}>
        {modules.map(m => {
          const cc = catColor[m.category] || '#c9a96e';
          const isOpen = selectedModule?.id === m.id;
          return (
            <div key={m.id} style={{ background: '#1a1208', border: `1px solid ${isOpen ? cc + '66' : '#3a2a12'}`, borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }} onClick={() => setSelectedModule(isOpen ? null : m)}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: m.status === 'published' ? '#10b981' : '#f59e0b', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f5e6c8', fontWeight: 700 }}>{m.title}</div>
                  <div style={{ color: '#5a4030', fontSize: 12 }}>{m.category} · {m.questions?.length || 0} questions · {m.videos?.length || 0} videos · Pass: {m.pass_mark}%</div>
                </div>
                <span style={{ color: '#5a4030', fontSize: 12, background: m.status === 'published' ? '#10b98118' : '#f59e0b18', padding: '3px 10px', borderRadius: 20, border: `1px solid ${m.status === 'published' ? '#10b98144' : '#f59e0b44'}`, color: m.status === 'published' ? '#10b981' : '#f59e0b' }}>{m.status}</span>
                <span style={{ color: '#5a4030' }}>{isOpen ? '▲' : '▼'}</span>
              </div>

              {isOpen && (
                <div style={{ borderTop: '1px solid #3a2a12', padding: '16px 20px' }}>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                    <Btn small variant="ghost" onClick={() => { setEditingModule(m); setShowModuleEditor(true); }}>✏️ Edit Module</Btn>
                    <Btn small variant="ghost" onClick={() => setContentModule(m)}>📝 Edit Content</Btn>
                    <Btn small variant="ghost" onClick={() => { setShowVideoEditor(true); }}>+ Add Video</Btn>
                    <Btn small variant="ghost" onClick={() => { setShowDocEditor(true); }}>+ Add Document</Btn>
                    <Btn small variant="ghost" onClick={() => { setEditingQuestion(null); setShowQuestionEditor(true); }}>+ Add Question</Btn>
                  </div>

                  {/* Videos */}
                  {m.videos?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ color: '#8a7055', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Videos ({m.videos.length})</div>
                      {m.videos.map(v => (
                        <div key={v.id} style={{ background: '#2a1f0f', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 16 }}>▶</span>
                          <span style={{ color: '#f5e6c8', fontSize: 13, flex: 1 }}>{v.title}</span>
                          <span style={{ color: '#5a4030', fontSize: 12 }}>{v.duration}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Questions */}
                  {m.questions?.length > 0 && (
                    <div>
                      <div style={{ color: '#8a7055', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Quiz Questions ({m.questions.length})</div>
                      {m.questions.map((q, i) => (
                        <div key={q.id} style={{ background: '#2a1f0f', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <span style={{ color: '#c9a96e', fontSize: 12, flexShrink: 0, marginTop: 1 }}>{i + 1}.</span>
                          <span style={{ color: '#d4b896', fontSize: 13, flex: 1, lineHeight: 1.4 }}>{q.question}</span>
                          <Btn small variant="secondary" onClick={() => { setEditingQuestion(q); setShowQuestionEditor(true); }}>Edit</Btn>
                        </div>
                      ))}
                    </div>
                  )}

                  {!m.questions?.length && !m.videos?.length && (
                    <p style={{ color: '#5a4030', fontSize: 13 }}>No content yet. Add videos and quiz questions to get started.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
